import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, IconButton, Tooltip, Chip, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Checkbox, FormControlLabel, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Replay as ResendIcon,
  Stop as StopIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { AdminListPanel } from './AdminListPanel';
import { api } from '../../api/rpc';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import type { FaxQueueItem } from '../../api/types';

const STATUS_FILTERS = ['ALL', 'QUEUED', 'IN_PROGRESS', 'SUCCESSFUL', 'FAILED'] as const;

const columns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 80 },
  { field: 'patientName', headerName: 'Patient', flex: 1, minWidth: 150 },
  { field: 'faxNumber', headerName: 'Fax Number', width: 130 },
  {
    field: 'status',
    headerName: 'Status',
    width: 100,
    renderCell: (params) => {
      const s = String(params.value ?? '');
      const color = s === 'FAILED' ? 'error' : s === 'QUEUED' ? 'warning' : s === 'SUCCESSFUL' ? 'success' : s === 'IN_PROGRESS' ? 'info' : 'default';
      return <Chip label={s || '—'} size="small" color={color} sx={{ fontSize: 10, height: 18 }} />;
    },
  },
  { field: 'attempts', headerName: 'Attempts', width: 70 },
  {
    field: 'date',
    headerName: 'Date',
    width: 160,
    valueFormatter: (value: number | string | undefined) => {
      if (!value) return '—';
      const d = typeof value === 'number' ? new Date(value) : new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
  },
  { field: 'error', headerName: 'Error', flex: 1, minWidth: 150 },
];

/**
 * Fax Queue admin tab — FaxQueueListPanel equivalent.
 * Lists queued/sent fax reports with Resend and Stop actions, auto-refresh, and status filter.
 */
export const FaxQueuePanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string>() });
  const [confirmDialog, setConfirmDialog] = useState<{ action: string; fn: () => Promise<void> } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [detailItem, setDetailItem] = useState<FaxQueueItem | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data, isLoading } = useQuery<FaxQueueItem[]>({
    queryKey: ['faxqueue'],
    queryFn: async () => {
      try {
        const result = await api.getFaxQueue();
        return result.records ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 30_000,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['faxqueue'] });
  }, [queryClient]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(refresh, 15_000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refresh]);

  const items = useMemo(() => {
    let all = data ?? [];
    if (statusFilter !== 'ALL') {
      all = all.filter((f) => (f.status ?? '').toUpperCase() === statusFilter);
    }
    if (!search) return all;
    const s = search.toLowerCase();
    return all.filter(
      (f) =>
        (f.patientName ?? '').toLowerCase().includes(s) ||
        (f.faxNumber ?? '').toLowerCase().includes(s) ||
        (f.status ?? '').toLowerCase().includes(s) ||
        (f.error ?? '').toLowerCase().includes(s),
    );
  }, [data, search, statusFilter]);

  // Status counts
  const statusCounts = useMemo(() => {
    const all = data ?? [];
    const counts: Record<string, number> = { ALL: all.length };
    for (const f of all) {
      const st = (f.status ?? 'UNKNOWN').toUpperCase();
      counts[st] = (counts[st] ?? 0) + 1;
    }
    return counts;
  }, [data]);

  const selectedIds = useMemo(
    () => (selected.type === 'include' ? [...selected.ids] as string[] : []),
    [selected],
  );
  const hasSelection = selectedIds.length > 0;

  const runAction = useCallback(async (action: string, fn: () => Promise<unknown>) => {
    setConfirmDialog(null);
    try {
      await fn();
      pushSuccess(`${action} completed successfully`);
    } catch (e) {
      pushError(`${action} failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    refresh();
  }, [refresh]);

  const askConfirm = useCallback((action: string, fn: () => Promise<unknown>) => {
    setConfirmDialog({ action, fn: () => runAction(action, fn) });
  }, [runAction]);

  const toolbar = (
    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
      <Tooltip title="Resend selected faxes">
        <span>
          <IconButton size="small" disabled={!hasSelection} onClick={() => askConfirm('Resend', () => api.resendFaxes(selectedIds))} sx={{ width: 28, height: 28 }}>
            <ResendIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Stop sending selected faxes">
        <span>
          <IconButton size="small" disabled={!hasSelection} onClick={() => askConfirm('Stop', () => api.stopSendingFaxes(selectedIds))} sx={{ width: 28, height: 28, color: hasSelection ? 'error.main' : undefined }}>
            <StopIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <>
      <AdminListPanel
        title="Fax Queue"
        search={search}
        onSearchChange={setSearch}
        onRefresh={refresh}
        toolbar={toolbar}
        statusText={`${items.length} fax jobs${hasSelection ? ` · ${selectedIds.length} selected` : ''}`}
      >
        {/* Status filter bar + auto-refresh */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25, gap: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            size="small" value={statusFilter} exclusive
            onChange={(_, v) => { if (v) setStatusFilter(v); }}
            sx={{ height: 22 }}
          >
            {STATUS_FILTERS.map((sf) => (
              <ToggleButton key={sf} value={sf} sx={{ fontSize: 10, px: 1, py: 0, textTransform: 'none' }}>
                {sf === 'ALL' ? 'All' : sf === 'IN_PROGRESS' ? 'In Progress' : sf.charAt(0) + sf.slice(1).toLowerCase()}
                {statusCounts[sf] != null ? ` (${statusCounts[sf]})` : ''}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <FormControlLabel
            control={<Checkbox size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} sx={{ p: 0.25 }} />}
            label={<Typography sx={{ fontSize: 10 }}>Auto-refresh (15s)</Typography>}
            sx={{ ml: 0 }}
          />
        </Box>

        <DataGrid
          rows={items}
          columns={columns}
          getRowId={(row) => (row as FaxQueueItem).id}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          checkboxSelection
          rowSelectionModel={selected}
          onRowSelectionModelChange={setSelected}
          onRowClick={(params) => setDetailItem(params.row as FaxQueueItem)}
          rowHeight={24}
          columnHeaderHeight={28}
          sx={{
            border: 'none',
            fontSize: 12,
            cursor: 'pointer',
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          }}
        />
      </AdminListPanel>

      {/* Confirm dialog */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs">
        <DialogTitle sx={{ fontSize: 14 }}>Confirm {confirmDialog?.action}</DialogTitle>
        <DialogContent sx={{ fontSize: 12 }}>
          {confirmDialog?.action} {selectedIds.length} fax(es)?
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmDialog(null)} sx={{ textTransform: 'none', fontSize: 12 }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={confirmDialog?.fn} sx={{ textTransform: 'none', fontSize: 12 }}>
            {confirmDialog?.action}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fax detail dialog */}
      {detailItem && (
        <Dialog open onClose={() => setDetailItem(null)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
          <DialogTitle sx={{ fontSize: 14, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
            Fax Details — {detailItem.id}
            <IconButton size="small" onClick={() => setDetailItem(null)} sx={{ width: 24, height: 24 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 2 }}>
            <DR l="ID" v={detailItem.id} />
            <DR l="Patient" v={detailItem.patientName ?? '—'} />
            <DR l="Fax Number" v={detailItem.faxNumber ?? '—'} />
            <DR l="Status" v={detailItem.status ?? '—'} />
            <DR l="Attempts" v={String(detailItem.attempts ?? '—')} />
            <DR l="Date" v={detailItem.date ? new Date(detailItem.date).toLocaleString() : '—'} />
            {detailItem.error && <DR l="Error" v={detailItem.error} />}
            {Object.entries(detailItem)
              .filter(([k]) => !['id', 'patientName', 'faxNumber', 'status', 'attempts', 'date', 'error'].includes(k) && !k.startsWith('_'))
              .map(([k, v]) => (
                <DR key={k} l={k} v={String(v ?? '—')} />
              ))}
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 1.5 }}>
            <Button size="small" onClick={() => setDetailItem(null)} sx={{ textTransform: 'none', fontSize: 12 }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const DR = ({ l, v }: { l: string; v: string }) => (
  <Box sx={{ display: 'flex', mb: 0.5, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 90, textAlign: 'right', color: 'text.secondary' }}>{l}:</Typography>
    <Typography variant="body2" sx={{ fontSize: 12 }}>{v}</Typography>
  </Box>
);
